import { Event } from "@/types/event";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EventListItem } from "./EventListItem";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

interface TodayEventsProps {
  events: Event[];
  selectedEvent: Event | null;
  onEventClick: (event: Event) => void;
  isLoading?: boolean;
}

const EVENTS_PER_PAGE = 5;

export const TodayEvents = ({ events, selectedEvent, onEventClick, isLoading }: TodayEventsProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="font-semibold sticky top-0 bg-background py-2 flex items-center gap-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
        <div className="space-y-2">
          {[1, 2].map((index) => (
            <div
              key={index}
              className="w-full p-4 rounded-lg border border-white/5"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <div className="flex items-center gap-1">
                      <Skeleton className="h-3 w-3 rounded-sm" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-14" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (events.length === 0) return null;

  const sortedEvents = [...events].sort((a, b) => b.score - a.score);
  const totalEvents = sortedEvents.length;
  const endIndex = currentPage * EVENTS_PER_PAGE;
  const visibleEvents = sortedEvents.slice(0, endIndex);
  const hasMoreEvents = totalEvents > endIndex;
  const hasPreviousEvents = currentPage > 1;

  const handleShowMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleShowLess = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  return (
    <div className="space-y-2">
      <div className="font-semibold sticky top-0 bg-background py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-primary">Today's Events</h3>
          <Badge variant="secondary" className="ml-2 bg-primary/20 text-primary">
            {events.length}
          </Badge>
        </div>
        
        {totalEvents > EVENTS_PER_PAGE && (
          <div className="text-xs text-white/60">
            {Math.min(endIndex, totalEvents)} of {totalEvents}
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        {visibleEvents.map((event) => (
          <EventListItem
            key={event.id}
            event={event}
            isSelected={selectedEvent?.id === event.id}
            onClick={onEventClick}
            isToday={true}
          />
        ))}
      </div>

      {/* Pagination Controls */}
      {(hasMoreEvents || hasPreviousEvents) && (
        <div className="flex items-center justify-center gap-2 py-2">
          {hasPreviousEvents && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShowLess}
              className="text-xs text-white/70 hover:text-white hover:bg-white/10"
            >
              <ChevronLeft className="h-3 w-3 mr-1" />
              Show Less
            </Button>
          )}
          
          {hasMoreEvents && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShowMore}
              className="text-xs text-white/70 hover:text-white hover:bg-white/10"
            >
              Show More
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};